import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { isManufacturerAdmin } from './services/users';
import Layout from './components/Layout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Devices from './pages/Devices';
import DeviceDetail from './pages/DeviceDetail';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import Users from './pages/Users';
import Billing from './pages/Billing';
import Admin from './pages/Admin';
import BoardCatalog from './pages/BoardCatalog';
import Manufacturers from './pages/Manufacturers';
import HardwareProviderDetail from './pages/HardwareProviderDetail';
import BoardListingEditor from './pages/BoardListingEditor';
import Listing from './pages/public/Listing';
import Integrations from './pages/Integrations';
import System from './pages/System';
import HardwareProviderDevices from './pages/HardwareProviderDevices';
import HardwareProviderOrders from './pages/HardwareProviderOrders';
import Store from './pages/Store';
import Purchases from './pages/Purchases';
import Cart from './pages/Cart';
import Authorize from './pages/Authorize';
import About from './pages/public/About';
import Contact from './pages/public/Contact';
import Home from './pages/public/Home';
import Privacy from './pages/public/Privacy';
import Developers from './pages/public/Developers';
import OpenSource from './pages/public/OpenSource';
import HardwareProviders from './pages/public/HardwareProviders';
import NotFound from './pages/NotFound';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // A manufacturer (marketplace seller) account only gets the seller surface:
  // their Hardware Provider page plus the left-nav Devices and Orders pages.
  // The API refuses these accounts everything company-scoped, so the router
  // sends any other path to their provider page instead of a wall of 403s.
  if (isManufacturerAdmin(user) && user.manufacturer_id != null) {
    const home = `/hardware-providers/${user.manufacturer_id}`;
    const onSellerSurface = location.pathname === home
      || location.pathname.startsWith(`${home}/`)
      || location.pathname === '/hardware-devices'
      || location.pathname === '/hardware-orders';
    if (!onSellerSurface) {
      return <Navigate to={home} replace />;
    }
  }

  return children;
}

// /companies/:id → /software-providers/:id, keeping the query string.
function LegacyCompanyRedirect() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/software-providers/${id}${search}`} replace />;
}

function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/"        element={<Home />} />
      {/* The catalog lives on /hardware; /product and /partner are old entry points */}
      <Route path="/product"    element={<Navigate to="/hardware" replace />} />
      <Route path="/partner"    element={<Navigate to="/hardware" replace />} />
      {/* Marketplace listing pages — published hardware from any provider */}
      <Route path="/product/:provider/:board" element={<Listing />} />
      <Route path="/product/:provider/:board/:variant" element={<Listing />} />
      <Route path="/about"      element={<About />} />
      <Route path="/contact"    element={<Contact />} />
      <Route path="/hardware"   element={<HardwareProviders />} />
      <Route path="/developers" element={<Developers />} />
      <Route path="/open-source" element={<OpenSource />} />
      <Route path="/privacy"    element={<Privacy />} />
      <Route path="/login"   element={<Login />} />
      <Route path="/signup"  element={<Signup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password"  element={<ResetPassword />} />

      {/* Protected — pathless layout route keeps /devices, /companies etc unchanged */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard"      element={<Dashboard />} />
        <Route path="/devices"        element={<Devices />} />
        <Route path="/devices/:id"    element={<DeviceDetail />} />
        {/* Marketplace parties: software providers (buyers — the companies
            tenant) and hardware providers (manufacturers — the sellers). */}
        <Route path="/software-providers"     element={<Companies />} />
        <Route path="/software-providers/:id" element={<CompanyDetail />} />
        <Route path="/hardware-providers"     element={<Manufacturers />} />
        <Route path="/hardware-providers/:id" element={<HardwareProviderDetail />} />
        <Route path="/hardware-providers/:id/boards/:boardId/listing" element={<BoardListingEditor />} />
        <Route path="/hardware-devices" element={<HardwareProviderDevices />} />
        <Route path="/hardware-orders" element={<HardwareProviderOrders />} />
        <Route path="/users"          element={<Users />} />
        <Route path="/system"         element={<System />} />
        <Route path="/store"          element={<Store />} />
        <Route path="/purchases"      element={<Purchases />} />
        <Route path="/cart"           element={<Cart />} />
        <Route path="/billing"        element={<Billing />} />
        <Route path="/admin"          element={<Admin />} />
        <Route path="/admin/boards"   element={<BoardCatalog />} />
        <Route path="/integrations"   element={<Integrations />} />

        {/* Back-compat redirects — old nav targets land on the new pages. */}
        <Route path="/companies"            element={<Navigate to="/software-providers" replace />} />
        <Route path="/companies/:id"        element={<LegacyCompanyRedirect />} />
        <Route path="/admin/manufacturers"  element={<Navigate to="/hardware-providers" replace />} />
        <Route path="/developer"          element={<Navigate to="/integrations" replace />} />
        {/* OAuth apps + per-user Connected Apps surfaces retired Sept 2026. */}
        <Route path="/oauth-applications" element={<Navigate to="/integrations" replace />} />
        <Route path="/connected-apps"     element={<Navigate to="/integrations" replace />} />

        <Route path="/authorize" element={<Authorize />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
